import React, { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { http, formatApiError } from "../api";
import { useConfirm } from "../components/ConfirmDialog";
import { useUpgrade } from "../UpgradeContext";
import UpgradeButton from "../components/UpgradeButton";

/**
 * Billing route — real Stripe-backed plan management.
 *
 *  Explorer  → current-plan summary + the Navigator upgrade card.
 *  Navigator → plan details (renewal, period, card on file),
 *              Manage payment / Switch period / Cancel actions,
 *              past_due banner if the renewal failed.
 *
 * Reads ?upgrade=success | cancel from the query (set by Stripe's return URLs)
 * and shows the right confirmation. The plan itself is updated by the webhook
 * — the success state here just polls /auth/me until the plan flips, with a
 * sensible 10s timeout if the webhook is slow.
 */
export default function Billing() {
  const { user, refreshMe } = useAuth();
  const confirm = useConfirm();
  const { open: openUpgrade } = useUpgrade();

  const plan = user?.plan_summary || {};
  const isNavigator = !!plan.is_navigator;
  const status = plan.status || (isNavigator ? "active" : "free");
  const period = plan.period || null;
  const renewsAt = plan.renews_at || null;
  const cancelAtPeriodEnd = !!plan.cancel_at_period_end;
  const cardBrand = plan.card_brand || null;
  const cardLast4 = plan.card_last4 || null;
  const cardExp = plan.card_exp || null;

  const [outcome, setOutcome] = useState("");   // 'success' | 'cancel' | ''
  const [polling, setPolling] = useState(false);
  const [busyAction, setBusyAction] = useState(""); // 'portal' | 'switch' | 'cancel' | ''
  const [err, setErr] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);

  // Parse the upgrade outcome from the URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const u = params.get("upgrade");
    if (u === "success") {
      setOutcome("success");
      // Webhook → plan flip takes a beat. Poll /auth/me until the user is Navigator
      // or we hit the 10s timeout. UI shows a "We're activating your account" state.
      let stopped = false;
      let attempts = 0;
      setPolling(true);
      const tick = async () => {
        attempts += 1;
        try {
          await refreshMe();
        } catch (e) { /* ignore */ }
        if (stopped) return;
        if (attempts >= 10) {
          setPolling(false);
          return;
        }
        setTimeout(tick, 1000);
      };
      tick();
      // Fire analytics
      try {
        http.post("/analytics/event", {
          event: "upgrade_checkout_returned_success",
          properties: { session_id: params.get("session_id") },
        }).catch(() => {});
      } catch (e) { /* ignore */ }
      return () => { stopped = true; };
    }
    if (u === "cancel") {
      setOutcome("cancel");
      try {
        http.post("/analytics/event", {
          event: "upgrade_checkout_cancelled",
        }).catch(() => {});
      } catch (e) { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the plan badge flips to Navigator, stop the polling loop
  useEffect(() => {
    if (isNavigator && polling) setPolling(false);
  }, [isNavigator, polling]);

  const openPortal = async () => {
    setBusyAction("portal");
    setErr("");
    try {
      const { data } = await http.post("/stripe/customer-portal", {
        return_url: `${window.location.origin}/app/billing`,
      });
      if (!data?.url) throw new Error("No portal URL.");
      window.location.href = data.url;
    } catch (e) {
      setErr(formatApiError(e));
      setBusyAction("");
    }
  };

  const switchPeriod = async () => {
    const target = period === "annual" ? "monthly" : "annual";
    const label = target === "annual" ? "annual ($132/year, save $48)" : "monthly ($11/month)";
    const ok = await confirm({
      title: `Switch to ${label}?`,
      message:
        "Stripe will prorate the change — you'll get a small credit or charge for the unused portion of your current period.",
      confirmText: `Switch to ${target}`,
    });
    if (!ok) return;
    setBusyAction("switch");
    setErr("");
    try {
      await http.post("/stripe/subscription/change-period", { period: target });
      await refreshMe();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusyAction("");
    }
  };

  return (
    <div data-testid="billing-view" style={{ maxWidth: 880, margin: "0 auto" }}>
      <div style={{ marginBottom: 24 }}>
        <div className="eyebrow">Settings</div>
        <h1 style={{ marginTop: 8, fontSize: 32 }}>
          Billing & <em>plan</em>
        </h1>
        <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          Manage your subscription and unlock Navigator features.
        </p>
      </div>

      {/* Upgrade outcome banner */}
      {outcome === "success" && (
        <SuccessBanner polling={polling} isNavigator={isNavigator} />
      )}
      {outcome === "cancel" && (
        <CancelOutcomeBanner onTryAgain={() => openUpgrade("", "billing_cancel_retry")} />
      )}

      {/* Past-due banner */}
      {isNavigator && status === "past_due" && (
        <div className="billing-past-due-banner" data-testid="billing-past-due-banner">
          <div>
            <strong>Your last payment didn't go through.</strong>
            <p>
              We'll keep Navigator unlocked while we retry, but Stripe will eventually
              cancel if it can't charge a working card. Update your payment method to
              stay on Navigator.
            </p>
          </div>
          <button
            className="btn btn-clay"
            onClick={openPortal}
            disabled={busyAction === "portal"}
            data-testid="billing-past-due-update-btn"
          >
            {busyAction === "portal" ? "Opening…" : "Update payment method"}
          </button>
        </div>
      )}

      {/* Scheduled-cancel banner */}
      {isNavigator && cancelAtPeriodEnd && renewsAt && (
        <div className="billing-cancel-banner" data-testid="billing-cancel-banner">
          <div>
            <strong>Navigator ends {fmtDate(renewsAt)}.</strong>
            <p>
              Your subscription is set to cancel at the end of the current period.
              Change your mind and you can keep going.
            </p>
          </div>
          <button
            className="btn btn-ghost"
            onClick={async () => {
              setBusyAction("resume");
              try {
                await http.post("/stripe/subscription/resume");
                await refreshMe();
              } catch (e) {
                setErr(formatApiError(e));
              } finally {
                setBusyAction("");
              }
            }}
            data-testid="billing-resume-btn"
          >
            {busyAction === "resume" ? "Resuming…" : "Keep my plan"}
          </button>
        </div>
      )}

      {/* Current plan card */}
      <div className="card" style={{ padding: 24, marginBottom: 22 }} data-testid="billing-current-card">
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div className="eyebrow">Current plan</div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 600, marginTop: 6 }}>
              {isNavigator ? "Navigator" : "Explorer"}
            </div>
            <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
              {!isNavigator && "Track 1 university · 10 AI advisor messages/day · basic scholarship search."}
              {isNavigator && period === "monthly" && "$11 / month · billed monthly · cancel anytime"}
              {isNavigator && period === "annual" && "$11 / month · billed $132 annually · save $48"}
              {isNavigator && !period && "Unlimited universities, advisor, and every feature."}
            </div>
            {isNavigator && renewsAt && !cancelAtPeriodEnd && (
              <div className="muted" style={{ fontSize: 12.5, marginTop: 6 }} data-testid="billing-renews-at">
                {status === "active" && <>Renews on {fmtDate(renewsAt)}</>}
                {status === "past_due" && <>Retry scheduled — last attempt failed</>}
              </div>
            )}
            {isNavigator && cardLast4 && (
              <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }} data-testid="billing-card">
                {cardBrand ? cardBrand.toUpperCase() : "Card"} ending in {cardLast4}
                {cardExp ? ` · exp ${cardExp}` : ""}
              </div>
            )}
          </div>
          {isNavigator ? (
            <div className="pill" style={{ background: "var(--ink)", color: "var(--paper)", padding: "6px 12px" }}>
              <span style={{ background: "var(--orange)", width: 8, height: 8, borderRadius: 999, display: "inline-block", marginRight: 6 }} />
              {status === "past_due" ? "Past due" : cancelAtPeriodEnd ? "Cancels soon" : "Active"}
            </div>
          ) : (
            <UpgradeButton
              feature=""
              context="billing_current_card"
              label="Upgrade to Navigator"
              testid="billing-upgrade-btn"
            />
          )}
        </div>
      </div>

      {/* Navigator: management actions */}
      {isNavigator && (
        <div className="card" style={{ padding: 24, marginBottom: 22 }} data-testid="billing-manage-card">
          <div className="eyebrow">Manage subscription</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 12 }}>
            <button
              className="btn btn-ghost"
              onClick={openPortal}
              disabled={busyAction === "portal"}
              data-testid="billing-manage-payment-btn"
            >
              {busyAction === "portal" ? "Opening…" : "Manage payment method"}
            </button>
            <button
              className="btn btn-ghost"
              onClick={switchPeriod}
              disabled={busyAction === "switch" || cancelAtPeriodEnd}
              data-testid="billing-switch-period-btn"
            >
              {busyAction === "switch"
                ? "Switching…"
                : `Switch to ${period === "annual" ? "monthly" : "annual"}`}
            </button>
            {!cancelAtPeriodEnd && (
              <button
                className="btn btn-ghost"
                onClick={() => setCancelOpen(true)}
                disabled={busyAction === "cancel"}
                data-testid="billing-cancel-btn"
                style={{ color: "var(--clay-deep)" }}
              >
                Cancel subscription
              </button>
            )}
          </div>
          {err && <div className="auth-error" style={{ marginTop: 12 }} data-testid="billing-error">{err}</div>}
          <p className="muted" style={{ fontSize: 11.5, marginTop: 14, marginBottom: 0 }}>
            Payment processing and card storage are handled by Stripe. We never see your card details.
          </p>
        </div>
      )}

      {/* Explorer: Navigator upgrade card */}
      {!isNavigator && <ExplorerUpgradeCard openUpgrade={openUpgrade} />}

      {cancelOpen && (
        <CancelModal
          onClose={() => setCancelOpen(false)}
          renewsAt={renewsAt}
          period={period}
          onConfirmed={async () => {
            setCancelOpen(false);
            await refreshMe();
          }}
        />
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch (e) {
    return iso;
  }
}

function SuccessBanner({ polling, isNavigator }) {
  return (
    <div className="billing-success-banner" data-testid="billing-success-banner">
      <div className="billing-success-tick" aria-hidden>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
          <path d="M5 12l5 5L20 7" />
        </svg>
      </div>
      <div>
        <strong>You're on Navigator.</strong>
        <p>
          {isNavigator
            ? "Every gated feature is unlocked. Welcome aboard."
            : polling
              ? "Activating your account… your plan badge will flip to Navigator in a moment."
              : "If your account doesn't update in a minute, refresh — the webhook may have been delayed."}
        </p>
      </div>
    </div>
  );
}

function CancelOutcomeBanner({ onTryAgain }) {
  return (
    <div className="billing-cancel-outcome-banner" data-testid="billing-cancel-outcome-banner">
      <div>
        <strong>No charge — you didn't complete checkout.</strong>
        <p>Still on Explorer. Whenever you're ready, the upgrade is one click away.</p>
      </div>
      <button className="btn btn-clay" onClick={onTryAgain} data-testid="billing-retry-upgrade-btn">
        Try again
      </button>
    </div>
  );
}

function ExplorerUpgradeCard({ openUpgrade }) {
  const [billing, setBilling] = useState("annual");
  const price = billing === "annual" ? "$11" : "$14.99";
  const billingNote =
    billing === "annual" ? "billed $132 annually · save $48" : "billed monthly · cancel anytime";

  const features = [
    "Unlimited universities on your board",
    "Unlimited AI advisor conversations",
    "Full profile unlock",
    "Fit score for every university",
    "Admission probability",
    "Aid gap analysis",
    "Gmail drafting + one-click outreach",
    "Full scholarship database",
    "AI-generated prioritised to-do lists",
    "Priority support",
  ];

  return (
    <div className="card billing-upgrade-card" style={{ padding: 28 }} data-testid="billing-navigator-card">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
        <div>
          <div className="eyebrow">Navigator</div>
          <h2 style={{ margin: "4px 0 0 0", fontFamily: "var(--font-display)" }}>
            For students <em>actually applying</em>.
          </h2>
        </div>
        <div className="billing-toggle" role="tablist" aria-label="Billing cycle" style={{ display: "inline-flex", gap: 4, padding: 4, background: "var(--bg-sunk)", borderRadius: 999 }}>
          <button
            type="button"
            onClick={() => setBilling("monthly")}
            className={`u-toggle ${billing === "monthly" ? "active" : ""}`}
            data-testid="billing-toggle-monthly"
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBilling("annual")}
            className={`u-toggle ${billing === "annual" ? "active" : ""}`}
            data-testid="billing-toggle-annual"
          >
            Annual <span className="save-pill">save $48</span>
          </button>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 8 }}>
        <span style={{ fontFamily: "var(--font-display)", fontSize: 48, fontWeight: 600 }} data-testid="billing-price">{price}</span>
        <span className="muted" style={{ fontSize: 14 }}>/month</span>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 2 }} data-testid="billing-note">{billingNote}</div>

      <ul style={{ listStyle: "none", padding: 0, margin: "20px 0", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
        {features.map((f, i) => (
          <li key={i} style={{ fontSize: 14, display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ color: "var(--orange)", flex: "0 0 14px" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l5 5L20 7" /></svg>
            </span>
            {f}
          </li>
        ))}
      </ul>

      <UpgradeButton
        feature=""
        context={`billing_card_${billing}`}
        label="Start applying smarter"
        size="lg"
        testid="billing-start-btn"
      />
      <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>Cancel anytime · 7-day money-back</p>
      <p className="muted" data-testid="billing-country-disclaimer" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
        Not in a{" "}
        <a href="https://stripe.com/global" target="_blank" rel="noopener noreferrer" style={{ color: "var(--orange)" }}>Stripe-supported country</a>? Email{" "}
        <a href="mailto:hello@govidhya.com?subject=Upgrade%20my%20account" style={{ color: "var(--orange)" }}>hello@govidhya.com</a> to upgrade your account.
      </p>
    </div>
  );
}

/* ─── Cancel-with-reason modal ─── */
const CANCEL_REASONS = [
  { id: "too_expensive", label: "Too expensive" },
  { id: "not_using_enough", label: "Not using it enough" },
  { id: "switched_service", label: "Switching to another tool" },
  { id: "other", label: "Other" },
];

function CancelModal({ onClose, renewsAt, period, onConfirmed }) {
  const [reason, setReason] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!reason) {
      setErr("Pick a reason so we can improve.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await http.post("/stripe/subscription/cancel", { reason, text });
      try {
        http.post("/analytics/event", {
          event: "subscription_cancelled_ui",
          properties: { reason, period: period || "" },
        }).catch(() => {});
      } catch (e) { /* ignore */ }
      await onConfirmed();
    } catch (e) {
      setErr(formatApiError(e));
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="cancel-modal">
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="eyebrow">Cancel Navigator</div>
        <h3 style={{ marginTop: 6 }}>Sorry to see you go.</h3>
        <p style={{ color: "var(--ink-3)", fontSize: 14, lineHeight: 1.55, marginTop: 6 }}>
          {renewsAt
            ? <>Your subscription will end on <strong>{fmtDate(renewsAt)}</strong>. You keep full access until then.</>
            : <>Your subscription will cancel at the end of the current period — full access until then.</>}
        </p>

        <div style={{ marginTop: 18 }}>
          <div className="eyebrow" style={{ fontSize: 10.5 }}>Why are you leaving?</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            {CANCEL_REASONS.map((r) => (
              <label key={r.id} className="cancel-reason-row" data-testid={`cancel-reason-${r.id}`}>
                <input
                  type="radio"
                  name="cancel_reason"
                  value={r.id}
                  checked={reason === r.id}
                  onChange={() => setReason(r.id)}
                />
                {r.label}
              </label>
            ))}
          </div>
          <textarea
            className="input"
            placeholder="(Optional) anything else we should know?"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            data-testid="cancel-text"
            style={{ marginTop: 10, width: "100%", resize: "vertical" }}
          />
        </div>

        {err && <div className="auth-error" style={{ marginTop: 10 }}>{err}</div>}

        <div style={{ display: "flex", gap: 8, marginTop: 18, justifyContent: "flex-end" }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy} data-testid="cancel-keep-btn">
            Keep my plan
          </button>
          <button
            className="btn"
            onClick={submit}
            disabled={busy || !reason}
            style={{ background: "var(--clay-deep)", color: "var(--paper)" }}
            data-testid="cancel-confirm-btn"
          >
            {busy ? "Cancelling…" : "Confirm cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}
